import { useEffect, useRef, useState } from 'react';
import { logger } from './logger';

/**
 * Subscribes to backend WebSocket updates. Auto-reconnects with backoff.
 * Returns the latest scanner_update message and connection status.
 *
 * Path: same-origin /api/ws (proxied via ingress, cookies are sent).
 */
export function useScannerStream({ enabled = true } = {}) {
    const [data, setData] = useState({ short: null, medium: null });
    const [favorites, setFavorites] = useState({ list: [], events: [], ts: null });
    // tickPrices: { SYMBOL: { price, change_pct_24h, ts, prev } }
    // prev is kept so UI can briefly flash green/red on change.
    const [tickPrices, setTickPrices] = useState({});
    const [status, setStatus] = useState('connecting');
    const wsRef = useRef(null);
    const retryRef = useRef(0);
    const closedByUsRef = useRef(false);

    useEffect(() => {
        if (!enabled) return undefined;

        const connect = () => {
            const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
            const u = new URL(base);
            const url = `${proto}//${u.host}/api/ws`;
            const ws = new WebSocket(url);
            wsRef.current = ws;

            ws.onopen = () => {
                retryRef.current = 0;
                setStatus('open');
            };
            ws.onmessage = (ev) => {
                try {
                    const msg = JSON.parse(ev.data);
                    if (msg.type === 'scanner_update') {
                        setData((prev) => ({ ...prev, [msg.timeframe]: msg }));
                    } else if (msg.type === 'favorite_update') {
                        setFavorites({
                            list: msg.favorites || [],
                            events: msg.level_events || [],
                            ts: msg.ts || Date.now() / 1000,
                        });
                    } else if (msg.type === 'price_tick') {
                        // Merge: keep previous price so UI can flash on diff
                        setTickPrices((prev) => {
                            const next = { ...prev };
                            const incoming = msg.prices || {};
                            Object.entries(incoming).forEach(([sym, p]) => {
                                const prevP = prev[sym]?.price;
                                next[sym] = { ...p, prev: prevP };
                            });
                            return next;
                        });
                    }
                    // ping is no-op
                } catch (e) {
                    logger.warn('ws message parse failed', e);
                }
            };
            ws.onclose = (ev) => {
                wsRef.current = null;
                if (closedByUsRef.current) return;
                if (ev.code === 4401) {
                    setStatus('unauthorized');
                    return;
                }
                setStatus('reconnecting');
                const delay = Math.min(15000, 1000 * 2 ** retryRef.current);
                retryRef.current += 1;
                setTimeout(connect, delay);
            };
            ws.onerror = () => {
                setStatus('reconnecting');
            };
        };

        connect();

        return () => {
            closedByUsRef.current = true;
            try {
                wsRef.current?.close();
            } catch (e) {
                logger.warn('ws close failed', e);
            }
        };
    }, [enabled]);

    return { data, favorites, tickPrices, status };
}
